import React from 'react';
import { ResponsiveContainer, LineChart, Line, Tooltip } from 'recharts';
import { Card, CardHeader, MetricValue } from './Card';
import { InfoIcon } from '../icons/FinanceIcons';

// Custom Tooltip for main charts
export const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: 'none',
        zIndex: 1000
      }}>
        <p style={{ fontWeight: '600', margin: '0 0 8px 0' }}>{label}</p>
        {payload.map((entry, index) => (
          entry.value != null && entry.value !== 0 ? (
            <p key={index} style={{ color: entry.color || '#a3a3a3', margin: '4px 0' }}>
              {entry.dataKey === 'current' ? 'Current: ' :
               entry.dataKey === 'previous' ? 'Previous: ' :
               entry.name ? `${entry.name}: ` : ''}
              £{entry.value.toLocaleString()}
            </p>
          ) : null
        ))}
      </div>
    );
  }
  return null;
};

// Mini Line Chart Component (Stripe-style) with Tooltip
export const MiniLineChart = ({ data, color = '#8b5cf6', height = 120, valuePrefix = '', valueSuffix = '' }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  
  // Custom tooltip for mini charts
  const MiniTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1a1a1a',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '13px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: 'none'
        }}>
          <p style={{ fontWeight: '600', margin: '0 0 4px 0' }}>{payload[0]?.payload?.label || label}</p>
          <p style={{ color: '#a3a3a3', margin: 0 }}>
            {valuePrefix}{typeof payload[0]?.value === 'number' ? payload[0].value.toLocaleString() : payload[0]?.value}{valueSuffix}
          </p>
        </div>
      );
    }
    return null;
  };
  
  // Y-axis labels
  const yLabels = [max, Math.round((max + min) / 2), min];
  
  return (
    <div style={{ position: 'relative', height: `${height}px`, marginTop: '1rem' }}>
      {/* Y-axis labels */}
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af', zIndex: 1 }}>
        {yLabels.map((label, i) => (
          <span key={i}>{valuePrefix}{typeof label === 'number' && label >= 1000 ? `${(label/1000).toFixed(0)}k` : label}{valueSuffix}</span>
        ))}
      </div>
      
      {/* Chart area */}
      <div style={{ position: 'absolute', left: 0, right: '40px', top: 0, bottom: '24px' }}>
        {/* Grid lines */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ borderBottom: '1px solid #f3f4f6', width: '100%' }} />
          ))}
        </div>
        
        {/* Recharts Line */}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <Tooltip 
              content={<MiniTooltip />} 
              cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '4 4' }}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={color} 
              strokeWidth={2} 
              dot={false}
              activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* X-axis labels */}
      <div style={{ position: 'absolute', left: 0, right: '40px', bottom: 0, display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af' }}>
        <span>{data[0]?.label}</span>
        <span>{data[data.length - 1]?.label}</span>
      </div>
    </div>
  );
};

// Metric Card with Chart (Stripe-style)
export const MetricCardWithChart = ({ title, value, data, color = '#8b5cf6', updatedText = 'Updated 2 hours ago', prefix = '', suffix = '' }) => (
  <Card>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.25rem' }}>
      <h3 style={{ fontSize: '15px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{title}</h3>
      <InfoIcon />
    </div>
    <p style={{ fontSize: '28px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 0.5rem 0' }}>{prefix}{value}{suffix}</p>
    <MiniLineChart data={data} color={color} height={120} valuePrefix={prefix} valueSuffix={suffix} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem' }}>
      <span style={{ fontSize: '12px', color: '#9ca3af' }}>{updatedText}</span>
      <button style={{ fontSize: '13px', fontWeight: '500', color: '#1a1a1a', background: 'none', border: 'none', cursor: 'pointer' }}>More details</button>
    </div>
  </Card>
);

export default MiniLineChart;
